import { onUnmounted, shallowRef } from "vue";

/**
 * Composable for managing a WebSocket connection.
 *
 * @param url The WebSocket URL to connect to.
 * @param onMessage Optional callback function to handle incoming messages.
 * @returns An object with the socket state, and methods to connect and disconnect.
 */
export function useWebSocket(url: string, onMessage?: (event: MessageEvent) => void) {
  const socket = shallowRef<WebSocket | null>(null);
  let reconnectAttempts = 0;
  const maxReconnectAttempts = 5;

  const connect = () => {
    if (socket.value && socket.value.readyState === WebSocket.OPEN) {
      return;
    }

    socket.value = new WebSocket(url);

    socket.value.onopen = () => {
      reconnectAttempts = 0;
    };

    socket.value.onmessage = (event) => {
      if (onMessage) {
        onMessage(event);
      }
    };

    socket.value.onclose = () => {
      reconnect();
    };

    socket.value.onerror = (error) => {
      console.error("WebSocket error:", error);
    };
  };

  const reconnect = () => {
    if (reconnectAttempts < maxReconnectAttempts) {
      reconnectAttempts++;
      setTimeout(() => {
        connect();
      }, 1000 * reconnectAttempts);
    }
  };

  const disconnect = () => {
    if (socket.value) {
      socket.value.onclose = null;
      socket.value.close();
      socket.value = null;
    }
  };

  connect();

  onUnmounted(() => {
    disconnect();
  });

  return {
    socket,
    connect,
    disconnect,
  };
}
